import { QuestionType, Question } from '../types';
import { QuestionTypeConfig, QuestionRendererProps } from '../../practice/components/renderers/types';
import { getValidator } from './validatorRegistry';
import React from 'react';

// ============================================================
// Lazy Renderer Imports — Code-split per question type
// Each renderer is loaded on first use, reducing the initial bundle by one
// chunk per active question type (map_label_regions deprecated in Sessie 3).
// The authoritative type count is DERIVED at runtime as QUESTION_TYPE_COUNT
// (= Object.keys(QUESTION_TYPE_META).length) — never hand-write it in comments.
// IMPORTANT: import() paths MUST be static string literals for bundler analysis.
// ============================================================

const MultipleChoiceRenderer = React.lazy(() => import('../../practice/components/renderers/MultipleChoiceRenderer').then(m => ({
  default: m.MultipleChoiceRenderer
})));
const ColumnArithmeticRenderer = React.lazy(() => import('../../practice/components/renderers/ColumnArithmeticRenderer').then(m => ({
  default: m.ColumnArithmeticRenderer
})));
const LongDivisionRenderer = React.lazy(() => import('../../practice/components/renderers/LongDivisionRenderer').then(m => ({
  default: m.LongDivisionRenderer
})));
const MoneyAssemblerRenderer = React.lazy(() => import('../../practice/components/renderers/MoneyAssemblerRenderer').then(m => ({
  default: m.MoneyAssemblerRenderer
})));
const ClockRenderer = React.lazy(() => import('../../practice/components/renderers/ClockRenderer').then(m => ({
  default: m.ClockRenderer
})));
const ClockSetRenderer = React.lazy(() => import('../../practice/components/renderers/ClockSetRenderer').then(m => ({
  default: m.ClockSetRenderer
})));
const FillInRenderer = React.lazy(() => import('../../practice/components/renderers/FillInRenderer').then(m => ({
  default: m.FillInRenderer
})));
const OrderingRenderer = React.lazy(() => import('../../practice/components/renderers/OrderingRenderer').then(m => ({
  default: m.OrderingRenderer
})));
const MatchingRenderer = React.lazy(() => import('../../practice/components/renderers/MatchingRenderer').then(m => ({
  default: m.MatchingRenderer
})));
const DragDropFillRenderer = React.lazy(() => import('../../practice/components/renderers/DragDropFillRenderer').then(m => ({
  default: m.DragDropFillRenderer
})));
const TableReadingRenderer = React.lazy(() => import('../../practice/components/renderers/TableReadingRenderer').then(m => ({
  default: m.TableReadingRenderer
})));
const NumberSequenceRenderer = React.lazy(() => import('../../practice/components/renderers/NumberSequenceRenderer').then(m => ({
  default: m.NumberSequenceRenderer
})));
const RatioTableRenderer = React.lazy(() => import('../../practice/components/renderers/RatioTableRenderer').then(m => ({
  default: m.RatioTableRenderer
})));
const TextHighlightRenderer = React.lazy(() => import('../../practice/components/renderers/TextHighlightRenderer').then(m => ({
  default: m.TextHighlightRenderer
})));
const BarChartRenderer = React.lazy(() => import('../../practice/components/renderers/BarChartRenderer').then(m => ({
  default: m.BarChartRenderer
})));
const LineChartRenderer = React.lazy(() => import('../../practice/components/renderers/LineChartRenderer').then(m => ({
  default: m.LineChartRenderer
})));
const ScheduleRenderer = React.lazy(() => import('../../practice/components/renderers/ScheduleRenderer').then(m => ({
  default: m.ScheduleRenderer
})));
const PieChartRenderer = React.lazy(() => import('../../practice/components/renderers/PieChartRenderer').then(m => ({
  default: m.PieChartRenderer
})));
const MultiplicationGridRenderer = React.lazy(() => import('../../practice/components/renderers/MultiplicationGridRenderer').then(m => ({
  default: m.MultiplicationGridRenderer
})));
const TrueFalseSeriesRenderer = React.lazy(() => import('../../practice/components/renderers/TrueFalseSeriesRenderer').then(m => ({
  default: m.TrueFalseSeriesRenderer
})));
const EstimationSliderRenderer = React.lazy(() => import('../../practice/components/renderers/EstimationSliderRenderer').then(m => ({
  default: m.EstimationSliderRenderer
})));
const CategorizeRenderer = React.lazy(() => import('../../practice/components/renderers/CategorizeRenderer').then(m => ({
  default: m.CategorizeRenderer
})));
const ErrorSpottingRenderer = React.lazy(() => import('../../practice/components/renderers/ErrorSpottingRenderer').then(m => ({
  default: m.ErrorSpottingRenderer
})));
const GridShadeRenderer = React.lazy(() => import('../../practice/components/renderers/GridShadeRenderer').then(m => ({
  default: m.GridShadeRenderer
})));
const StepSolverRenderer = React.lazy(() => import('../../practice/components/renderers/StepSolverRenderer').then(m => ({
  default: m.StepSolverRenderer
})));
const SentenceParserRenderer = React.lazy(() => import('../../practice/components/renderers/SentenceParserRenderer').then(m => ({
  default: m.SentenceParserRenderer
})));
const ComparisonJudgeRenderer = React.lazy(() => import('../../practice/components/renderers/ComparisonJudgeRenderer').then(m => ({
  default: m.ComparisonJudgeRenderer
})));
const VerbConjugationFlowRenderer = React.lazy(() => import('../../practice/components/renderers/VerbConjugationFlowRenderer').then(m => ({
  default: m.VerbConjugationFlowRenderer
})));
const ThermometerSliderRenderer = React.lazy(() => import('../../practice/components/renderers/ThermometerSliderRenderer').then(m => ({
  default: m.ThermometerSliderRenderer
})));
const SentenceErrorJudgeRenderer = React.lazy(() => import('../../practice/components/renderers/SentenceErrorJudgeRenderer').then(m => ({
  default: m.SentenceErrorJudgeRenderer
})));
const MapLocateRegionRenderer = React.lazy(() => import('../../practice/components/renderers/MapLocateRegionRenderer').then(m => ({
  default: m.MapLocateRegionRenderer
})));
const MapLocatePointRenderer = React.lazy(() => import('../../practice/components/renderers/MapLocatePointRenderer').then(m => ({
  default: m.MapLocatePointRenderer
})));
const MapPlaceCitiesRenderer = React.lazy(() => import('../../practice/components/renderers/MapPlaceCitiesRenderer').then(m => ({
  default: m.MapPlaceCitiesRenderer
})));
const MapLocateLineRenderer = React.lazy(() => import('../../practice/components/renderers/MapLocateLineRenderer').then(m => ({
  default: m.MapLocateLineRenderer
})));
const SquareRootBuilderRenderer = React.lazy(() => import('../../practice/components/renderers/SquareRootBuilderRenderer').then(m => ({
  default: m.SquareRootBuilderRenderer
})));
const PlaceValueBuildRenderer = React.lazy(() => import('../../practice/components/renderers/PlaceValueBuildRenderer').then(m => ({
  default: m.PlaceValueBuildRenderer
})));
const BarModelPartRenderer = React.lazy(() => import('../../practice/components/renderers/BarModelPartRenderer').then(m => ({
  default: m.BarModelPartRenderer
})));
const StrategySolverRenderer = React.lazy(() => import('../../practice/components/renderers/StrategySolverRenderer').then(m => ({
  default: m.StrategySolverRenderer
})));
const FractionEquivalenceMatchRenderer = React.lazy(() => import('../../practice/components/renderers/FractionEquivalenceMatchRenderer').then(m => ({
  default: m.FractionEquivalenceMatchRenderer
})));
const FractionStripBuilderRenderer = React.lazy(() => import('../../practice/components/renderers/FractionStripBuilderRenderer').then(m => ({
  default: m.FractionStripBuilderRenderer
})));
const DoubleNumberLineRenderer = React.lazy(() => import('../../practice/components/renderers/DoubleNumberLineRenderer').then(m => ({
  default: m.DoubleNumberLineRenderer
})));
const CommaShiftRenderer = React.lazy(() => import('../../practice/components/renderers/CommaShiftRenderer').then(m => ({
  default: m.CommaShiftRenderer
})));
const ScientificNotationBuilderRenderer = React.lazy(() => import('../../practice/components/renderers/ScientificNotationBuilderRenderer').then(m => ({
  default: m.ScientificNotationBuilderRenderer
})));
const SpellingPickRenderer = React.lazy(() => import('../../practice/components/renderers/SpellingPickRenderer').then(m => ({
  default: m.SpellingPickRenderer
})));
const VerbEndingRenderer = React.lazy(() => import('../../practice/components/renderers/VerbEndingRenderer').then(m => ({
  default: m.VerbEndingRenderer
})));
const WordBuilderRenderer = React.lazy(() => import('../../practice/components/renderers/WordBuilderRenderer').then(m => ({
  default: m.WordBuilderRenderer
})));
const CoordinatePlotRenderer = React.lazy(() => import('../../practice/components/renderers/CoordinatePlotRenderer').then(m => ({
  default: m.CoordinatePlotRenderer
})));

/**
 * Question Type Registry v3.1
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/questionTypeRegistry.ts in R25.
 *
 * v3.2 (mei 2026): All renderers converted to React.lazy() dynamic imports
 * for code splitting. Each renderer is loaded on first use. The authoritative
 * count of active question types is the DERIVED `QUESTION_TYPE_COUNT` export
 * (= Object.keys(QUESTION_TYPE_META).length) — never a hand-typed number here,
 * so the comment can no longer drift from the registry (detangle §12.E /
 * QUESTION_TYPES_VISUALS_FUTURE_AUDIT). map_label_regions was deprecated in
 * Sessie 3 (0 seed usage).
 * React.memo() wrapping removed from registry — lazy components are cached
 * after first load. Individual renderer implementations MAY still wrap themselves
 * in React.memo() for re-render isolation against frequently-rendering parents.
 *
 * Modern architecture with clean separation of concerns:
 * - questionTypeRegistry.ts → renderer mapping + metadata
 * - validatorRegistry.ts → all validation logic
 * - answerProtocol.ts → answer payload types + bridge functions
 */

/** Metadata-only config for a question type (no validation logic) */
interface QuestionTypeMeta {
  type: QuestionType;
  label: string;
  isInteractive: boolean;
  hasBuiltInVisuals: boolean;
  shouldShuffleOptions: boolean;
  /**
   * MIRROR — NOT the source of truth. The authoritative assessment/CITO
   * eligibility lives in the governance policy and is read via
   * `isAssessmentEligibleType()` (policyEngine), which the assessment domain,
   * orchestrator and assessmentHealthStage all consult. This flag is a
   * convenience mirror for content-domain reads (questionValidation.isCitoEligible,
   * admin TypesTab) that must stay consistent with that policy: assessment is
   * multiple_choice ONLY (see assessmentEligibility.ts invariant). Do NOT widen
   * this flag to "open up" the assessment pool — that is a deliberate, documented
   * invariant, not a limitation. Drift between this mirror and the policy is a bug.
   */
  isCitoEligible: boolean;
  validateAnswer: (userAnswer: string | number, question: Question) => boolean;
  Renderer: React.ComponentType<QuestionRendererProps>;
}
/**
 * SSOT for "which question types exist + their runtime properties".
 *
 * Governance derives from this — see src/governance/questionTypeDefinitions.ts.
 * In particular, diversityStage reads `isInteractive` from here so that adding
 * a new interactive renderer (which MUST be registered here or it can't render)
 * automatically counts toward diversity, without a parallel governance update.
 */
export const QUESTION_TYPE_META: Record<QuestionType, QuestionTypeMeta> = {
  multiple_choice: {
    type: 'multiple_choice',
    label: 'Meerkeuze',
    isInteractive: false,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: true,
    validateAnswer: (userAnswer, question) => validateAnswer('multiple_choice', userAnswer, question),
    Renderer: MultipleChoiceRenderer
  },
  cijferend_rekenen: {
    type: 'cijferend_rekenen',
    label: 'Cijferend Rekenen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('cijferend_rekenen', userAnswer, question),
    Renderer: ColumnArithmeticRenderer
  },
  cijferend_delen: {
    type: 'cijferend_delen',
    label: 'Cijferend Delen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('cijferend_delen', userAnswer, question),
    Renderer: LongDivisionRenderer
  },
  clock: {
    type: 'clock',
    label: 'Klokkijken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('clock', userAnswer, question),
    Renderer: ClockRenderer
  },
  clock_set: {
    type: 'clock_set',
    label: 'Klok Instellen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('clock', userAnswer, question),
    // Reuses clock validator
    Renderer: ClockSetRenderer
  },
  money: {
    type: 'money',
    label: 'Geld',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('money', userAnswer, question),
    Renderer: MoneyAssemblerRenderer
  },
  fill_in: {
    type: 'fill_in',
    label: 'Invulvraag',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('fill_in', userAnswer, question),
    Renderer: FillInRenderer
  },
  ordering: {
    type: 'ordering',
    label: 'Volgorde',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('ordering', userAnswer, question),
    Renderer: OrderingRenderer
  },
  matching: {
    type: 'matching',
    label: 'Koppelen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('matching', userAnswer, question),
    Renderer: MatchingRenderer
  },
  drag_drop_fill: {
    type: 'drag_drop_fill',
    label: 'Sleep en Plaats',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('drag_drop_fill', userAnswer, question),
    Renderer: DragDropFillRenderer
  },
  table_reading: {
    type: 'table_reading',
    label: 'Tabel Aflezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('table_reading', userAnswer, question),
    Renderer: TableReadingRenderer
  },
  number_sequence: {
    type: 'number_sequence',
    label: 'Getallenreeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('number_sequence', userAnswer, question),
    Renderer: NumberSequenceRenderer
  },
  ratio_table: {
    type: 'ratio_table',
    label: 'Verhoudingstabel',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('ratio_table', userAnswer, question),
    Renderer: RatioTableRenderer
  },
  text_highlight: {
    type: 'text_highlight',
    label: 'Tekst Markeren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('text_highlight', userAnswer, question),
    Renderer: TextHighlightRenderer
  },
  bar_chart: {
    type: 'bar_chart',
    label: 'Staafgrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('bar_chart', userAnswer, question),
    Renderer: BarChartRenderer
  },
  line_chart: {
    type: 'line_chart',
    label: 'Lijngrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('line_chart', userAnswer, question),
    Renderer: LineChartRenderer
  },
  schedule_reading: {
    type: 'schedule_reading',
    label: 'Rooster Lezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('schedule_reading', userAnswer, question),
    Renderer: ScheduleRenderer
  },
  pie_chart: {
    type: 'pie_chart',
    label: 'Cirkeldiagram',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('pie_chart', userAnswer, question),
    Renderer: PieChartRenderer
  },
  multiplication_grid: {
    type: 'multiplication_grid',
    label: 'Tafelrooster',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('multiplication_grid', userAnswer, question),
    Renderer: MultiplicationGridRenderer
  },
  true_false_series: {
    type: 'true_false_series',
    label: 'Waar/Onwaar Reeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('true_false_series', userAnswer, question),
    Renderer: TrueFalseSeriesRenderer
  },
  estimation_slider: {
    type: 'estimation_slider',
    label: 'Schatting',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('estimation_slider', userAnswer, question),
    Renderer: EstimationSliderRenderer
  },
  categorize: {
    type: 'categorize',
    label: 'Sorteren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('categorize', userAnswer, question),
    Renderer: CategorizeRenderer
  },
  error_spotting: {
    type: 'error_spotting',
    label: 'Fouten Zoeken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('error_spotting', userAnswer, question),
    Renderer: ErrorSpottingRenderer
  },
  grid_shade: {
    type: 'grid_shade',
    label: 'Raster Arceren',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('grid_shade', userAnswer, question),
    Renderer: GridShadeRenderer
  },
  step_solver: {
    type: 'step_solver',
    label: 'Stapsgewijs Oplossen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('step_solver', userAnswer, question),
    Renderer: StepSolverRenderer
  },
  sentence_parser: {
    type: 'sentence_parser',
    label: 'Zinsontleding',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('sentence_parser', userAnswer, question),
    Renderer: SentenceParserRenderer
  },
  comparison_judge: {
    type: 'comparison_judge',
    label: 'Vergelijken',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('comparison_judge', userAnswer, question),
    Renderer: ComparisonJudgeRenderer
  },
  verb_conjugation_flow: {
    type: 'verb_conjugation_flow',
    label: 'Werkwoord Vervoegen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('verb_conjugation_flow', userAnswer, question),
    Renderer: VerbConjugationFlowRenderer
  },
  thermometer_slider: {
    type: 'thermometer_slider',
    label: 'Thermometer Slider',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('thermometer_slider', userAnswer, question),
    Renderer: ThermometerSliderRenderer
  },
  sentence_error_judge: {
    type: 'sentence_error_judge',
    label: 'Zinsfout Herkennen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('sentence_error_judge', userAnswer, question),
    Renderer: SentenceErrorJudgeRenderer
  },
  map_locate_region: {
    type: 'map_locate_region',
    label: 'Klik op de Regio',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('map_locate_region', userAnswer, question),
    Renderer: MapLocateRegionRenderer
  },
  map_locate_point: {
    type: 'map_locate_point',
    label: 'Klik op de Plek',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('map_locate_point', userAnswer, question),
    Renderer: MapLocatePointRenderer
  },
  // NOTE: map_label_regions was DEPRECATED in Sessie 3 (mei 2026).
  // Reason: 0 seed-gebruik over alle TOPO-families, aspirationele type
  // zonder dedicated renderer (her-gebruikte MapLocateRegionRenderer).
  // Use case (meerdere regio's tegelijk labelen) is dekbaar met
  // meerdere achtereenvolgende map_locate_region vragen.
  // Zie docs/archive/VISUALS_QT_RULEBOOK_DEEP_AUDIT_2026.md §17.

  map_place_cities: {
    type: 'map_place_cities',
    label: 'Plaats Steden op de Kaart',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('map_place_cities', userAnswer, question),
    Renderer: MapPlaceCitiesRenderer
  },
  map_locate_line: {
    type: 'map_locate_line',
    label: 'Klik op de Rivier',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('map_locate_line', userAnswer, question),
    Renderer: MapLocateLineRenderer
  },
  square_root_builder: {
    type: 'square_root_builder',
    label: 'Wortel Bouwen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('square_root_builder', userAnswer, question),
    Renderer: SquareRootBuilderRenderer
  },
  place_value_build: {
    type: 'place_value_build',
    label: 'Plaats-waarde bouwen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('place_value_build', userAnswer, question),
    Renderer: PlaceValueBuildRenderer
  },
  bar_model_part: {
    type: 'bar_model_part',
    label: 'Strookmodel — deel berekenen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('bar_model_part', userAnswer, question),
    Renderer: BarModelPartRenderer
  },
  strategy_solver: {
    type: 'strategy_solver',
    label: 'Handig rekenen — strategie kiezen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('strategy_solver', userAnswer, question),
    Renderer: StrategySolverRenderer
  },
  fraction_equivalence_match: {
    type: 'fraction_equivalence_match',
    label: 'Breuken Koppelen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('fraction_equivalence_match', userAnswer, question),
    Renderer: FractionEquivalenceMatchRenderer
  },
  fraction_strip_builder: {
    type: 'fraction_strip_builder',
    label: 'Breukenstrook Bouwen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('fraction_strip_builder', userAnswer, question),
    Renderer: FractionStripBuilderRenderer
  },
  double_number_line: {
    type: 'double_number_line',
    label: 'Dubbele Getallenlijn',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('double_number_line', userAnswer, question),
    Renderer: DoubleNumberLineRenderer
  },
  comma_shift: {
    type: 'comma_shift',
    label: 'Komma Verschuiven',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('comma_shift', userAnswer, question),
    Renderer: CommaShiftRenderer
  },
  scientific_notation_builder: {
    type: 'scientific_notation_builder',
    label: 'Wetenschappelijke Notatie Bouwen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('scientific_notation_builder', userAnswer, question),
    Renderer: ScientificNotationBuilderRenderer
  },
  spelling_pick: {
    type: 'spelling_pick',
    label: 'Letterkeuze (Spelling)',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('spelling_pick', userAnswer, question),
    Renderer: SpellingPickRenderer
  },
  verb_ending: {
    type: 'verb_ending',
    label: 'Werkwoord-uitgang',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('verb_ending', userAnswer, question),
    Renderer: VerbEndingRenderer
  },
  word_builder: {
    type: 'word_builder',
    label: 'Woordbouwer',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('word_builder', userAnswer, question),
    Renderer: WordBuilderRenderer
  },
  coordinate_plot: {
    type: 'coordinate_plot',
    label: 'Punt Plaatsen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) => validateAnswer('coordinate_plot', userAnswer, question),
    Renderer: CoordinatePlotRenderer
  }
};

/**
 * Build the QUESTION_TYPE_REGISTRY by combining
 * metadata from QUESTION_TYPE_META with validators from ValidatorRegistry.
 */
export const QUESTION_TYPE_REGISTRY: Record<QuestionType, QuestionTypeConfig> = Object.fromEntries(Object.entries(QUESTION_TYPE_META).map(([key, meta]) => [key, {
  ...meta,
  validateAnswer: (userAnswer: string | number, question: Question) => getValidator(key)(userAnswer, question)
}])) as Record<QuestionType, QuestionTypeConfig>;

/**
 * Derived count of active question types.
 *
 * SSOT for "how many question types exist" — computed from QUESTION_TYPE_META,
 * never hand-maintained. Use this anywhere a count is needed (admin UI, docs
 * sync-tests) so the number cannot drift away from the registry.
 */
export const QUESTION_TYPE_COUNT = Object.keys(QUESTION_TYPE_META).length;

/**
 * Visual-role taxonomy — SSOT for governance.
 *
 * Governance Structural Refactor 2026 — Fase 1.
 *
 * Describes WHERE the visual surface of a question type lives:
 *   - 'embedded':    the renderer itself IS the visual answer surface
 *                    (e.g. clock, money, ordering, comparison_judge,
 *                    square_root_builder). These should be EXEMPT from
 *                    VISUAL_STIMULUS_REQUIRED — forcing a stimulusAid
 *                    produces redundant rendering.
 *   - 'contextual':  the renderer is text/buttons; any visual lives in
 *                    stimulusAids/supportAids (multiple_choice, fill_in).
 *   - 'scaffolding': hybrid — the renderer has light visual affordances
 *                    but a stimulus visual remains didactically valuable.
 *                    (Reserved for future types; none today.)
 *
 * SSOT consumer: src/governance/pipeline/stages/visualStage.ts derives
 * its exempt-from-stimulus set from this map. Add a new question type?
 * Add it here once — governance follows automatically.
 */
export type QuestionTypeVisualRole = 'embedded' | 'contextual' | 'scaffolding';
export const QUESTION_TYPE_VISUAL_ROLE: Record<QuestionType, QuestionTypeVisualRole> = {
  // Pure contextual types: visual (if any) comes from stimulusAids.
  multiple_choice: 'contextual',
  fill_in: 'contextual',
  // Embedded types: the renderer IS the visual surface.
  cijferend_rekenen: 'embedded',
  cijferend_delen: 'embedded',
  clock: 'embedded',
  clock_set: 'embedded',
  money: 'embedded',
  ordering: 'embedded',
  matching: 'embedded',
  drag_drop_fill: 'embedded',
  table_reading: 'embedded',
  number_sequence: 'embedded',
  ratio_table: 'embedded',
  text_highlight: 'embedded',
  bar_chart: 'embedded',
  line_chart: 'embedded',
  schedule_reading: 'embedded',
  pie_chart: 'embedded',
  multiplication_grid: 'embedded',
  true_false_series: 'embedded',
  estimation_slider: 'embedded',
  categorize: 'embedded',
  error_spotting: 'embedded',
  grid_shade: 'embedded',
  step_solver: 'embedded',
  sentence_parser: 'embedded',
  comparison_judge: 'embedded',
  verb_conjugation_flow: 'embedded',
  thermometer_slider: 'embedded',
  sentence_error_judge: 'embedded',
  map_locate_region: 'embedded',
  map_locate_point: 'embedded',
  map_locate_line: 'embedded',
  map_place_cities: 'embedded',
  square_root_builder: 'embedded',
  place_value_build: 'embedded',
  bar_model_part: 'embedded',
  strategy_solver: 'embedded',
  fraction_equivalence_match: 'embedded',
  fraction_strip_builder: 'embedded',
  double_number_line: 'embedded',
  comma_shift: 'embedded',
  scientific_notation_builder: 'embedded',
  spelling_pick: 'embedded',
  verb_ending: 'embedded',
  word_builder: 'embedded',
  coordinate_plot: 'embedded'
};

/**
 * Helper: get the visual role for a question type, with a safe fallback.
 * Returns 'contextual' for unknown types so governance treats them as
 * needing external visuals (defensive default).
 */
export function getQuestionTypeVisualRole(type: string): QuestionTypeVisualRole {
  return QUESTION_TYPE_VISUAL_ROLE[type as QuestionType] ?? 'contextual';
}

/**
 * Helper: returns all question types with the given visual role.
 * Used by rulebooks/index.ts to derive auto-exempt sets.
 */
export function getQuestionTypesByVisualRole(role: QuestionTypeVisualRole): QuestionType[] {
  return (Object.entries(QUESTION_TYPE_VISUAL_ROLE) as Array<[QuestionType, QuestionTypeVisualRole]>).filter(([, r]) => r === role).map(([t]) => t);
}
export function getQuestionTypeConfig(type: string): QuestionTypeConfig {
  if (type === 'geld_samenstellen' || type === 'klok_aflezen') {
    throw new Error(`Obsolete questionType "${type}" detected. Migrate to canonical "money" or "clock".`);
  }
  const config = QUESTION_TYPE_REGISTRY[type as QuestionType];
  if (!config) {
    throw new Error(`Unsupported questionType "${type}" in questionTypeRegistry.`);
  }
  return config;
}